import React, { useEffect, useRef, useState } from 'react';
import './StarRating.css'; // Import the CSS for styling
import { useTranslation } from '../i18n';

const StarRating = ({ rating, setRating }) => {
  const { t } = useTranslation();

  const [hoverRating, setHoverRating] = useState(0); // Tracks hover/touch effects
  const [isTouching, setIsTouching] = useState(false); // Tracks if user is interacting via touch
  const containerRef = useRef(null);

  // Calculate rating based on position (mouse or touch)
  const calculateRating = (x, starIndex, starWidth) => {
    const isHalf = x < starWidth / 2;
    return isHalf ? starIndex - 0.5 : starIndex;
  };

  const handleTouchStartOrMove = (e) => {
    const touch = e.touches[0];
    const target = document.elementFromPoint(touch.clientX, touch.clientY);

    if (target && target.classList.contains('star-container')) {
      const rect = target.getBoundingClientRect();
      const offsetX = touch.clientX - rect.left;
      const starIndex = parseInt(target.getAttribute('data-index'), 10);

      const newRating = calculateRating(offsetX, starIndex, rect.width);
      setHoverRating(newRating);
      setRating(newRating);
    }
  };

  const handleTouchEnd = () => {
    setIsTouching(false);
  };

  const handleMouseMove = (e, starIndex) => {
    if (!isTouching) {
      const rect = e.target.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      setHoverRating(calculateRating(offsetX, starIndex, rect.width));
    }
  };

  const handleClick = (e, starIndex) => {
    const rect = e.target.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const newRating = calculateRating(offsetX, starIndex, rect.width);
    setRating(newRating);
  };

  const handleMouseLeave = () => {
    if (!isTouching) {
      setHoverRating(0);
    }
  };

  // Add touch event listeners
  useEffect(() => {
    const element = containerRef.current;
    if (element) {
      element.addEventListener('touchstart', handleTouchStartOrMove, {
        passive: false,
      });
      element.addEventListener('touchmove', handleTouchStartOrMove, {
        passive: false,
      });
      element.addEventListener('touchend', handleTouchEnd);

      return () => {
        element.removeEventListener('touchstart', handleTouchStartOrMove);
        element.removeEventListener('touchmove', handleTouchStartOrMove);
        element.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, []);

  const clearInput = () => {
    setHoverRating('');
    setRating('');
  };

  const renderStars = () => {
    return [...Array(5)].map((_, index) => {
      const starIndex = index + 1;
      const isFullStar =
        hoverRating >= starIndex || (!hoverRating && rating >= starIndex);
      const isHalfStar =
        hoverRating === starIndex - 0.5 ||
        (!hoverRating && rating === starIndex - 0.5);

      return (
        <div
          id="rating"
          key={index}
          className="star-container"
          data-index={starIndex}
          onMouseMove={(e) => handleMouseMove(e, starIndex)}
          onClick={(e) => handleClick(e, starIndex)}
          onMouseLeave={handleMouseLeave}
          tabIndex={0}
          role="button"
          aria-label={`Rate ${starIndex} out of 5`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleClick(e, starIndex);
            }
          }}
        >
          <div
            className={`star ${isFullStar ? 'full' : isHalfStar ? 'half' : ''}`}
          ></div>
        </div>
      );
    });
  };

  return (
    <div className="form-floating padding-right">
      <div className="form-control star-rating" ref={containerRef}>
        {renderStars()}
      </div>
      <label htmlFor="rating" className="form-label">
        {t.rating}
      </label>
      <div className="show-password-btn fs-s pt-65" onClick={clearInput}>
        {t.clear}
      </div>
    </div>
  );
};

export default StarRating;
